// UI-string localization (separate from content localization -- see docs/LOCALIZATION.md).
// Only a subset of the app's copy is wired through this yet (nav, login, settings tab labels)
// -- most pages still have hardcoded English JSX. Converting the rest is tracked as a known gap,
// not silently assumed done.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import es from './locales/es.json';
import hi from './locales/hi.json';
import zh from './locales/zh.json';
import type { Locale } from '../api/types';

const STORAGE_KEY = 'omnist.locale';
const SUPPORTED: Locale[] = ['en', 'es', 'hi', 'zh'];

function detectInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored as Locale)) return stored as Locale;
  } catch {
    // localStorage can throw in some private-browsing contexts -- fall through to detection.
  }
  const browserLang = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return SUPPORTED.includes(browserLang as Locale) ? (browserLang as Locale) : 'en';
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, es: { translation: es }, hi: { translation: hi }, zh: { translation: zh } },
  lng: detectInitialLocale(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

/** Switches the app's UI language and remembers it for next visit (pre-login pages have no
 * signed-in profile to read a preference from). Once signed in, UserContext additionally syncs
 * this to the caller's stored `locale` field via PUT /api/me -- see its `setLocale`. */
export function setAppLanguage(locale: Locale): void {
  void i18n.changeLanguage(locale);
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // best-effort persistence only
  }
}

export { SUPPORTED as SUPPORTED_LOCALES };
export default i18n;
